import { useEffect, useState, useCallback } from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, LineChart, Line,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { eur, eurShort, CHART_COLORS } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const YEARS = [2024, 2025, 2026, 2027];
const tooltipStyle = { background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 13 };

export default function Analisi() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [year, setYear] = useState(new Date().getFullYear());
  const [cashflow, setCashflow] = useState([]);
  const [breakdown, setBreakdown] = useState([]);
  const [byCantiere, setByCantiere] = useState([]);
  const [dettaglio, setDettaglio] = useState([]);

  const load = useCallback(async () => {
    const [cf, bd, bc, dt] = await Promise.all([
      api.get("/analytics/cashflow", { year }),
      api.get("/analytics/cost-breakdown", { year }),
      api.get("/analytics/by-cantiere", { year }),
      api.get("/analytics/cantiere-dettaglio", { year }),
    ]);
    setCashflow(cf); setBreakdown(bd); setByCantiere(bc); setDettaglio(dt);
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const totalCosti = breakdown.reduce((s, b) => s + b.value, 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Analisi Costi & Margini</h1>
          <p className="text-sm text-muted-foreground mt-1">Approfondisci l'andamento economico dell'impresa.</p>
        </div>
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="w-[110px]" data-testid="analisi-year-select"><SelectValue /></SelectTrigger>
          <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">{isOwner ? "Andamento Utile Mensile" : "Andamento Uscite Mensili"} {year}</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={cashflow} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="mese" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={eurShort} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v, n) => [eur(v), n]} contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {isOwner && <Line type="monotone" dataKey="entrate" name="Entrate" stroke="#10B981" strokeWidth={2.5} dot={false} />}
              <Line type="monotone" dataKey="uscite" name="Uscite" stroke="#D97706" strokeWidth={2.5} dot={false} />
              {isOwner && <Line type="monotone" dataKey="utile" name="Utile" stroke="#3B82F6" strokeWidth={2.5} dot={{ r: 3 }} />}
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Ripartizione Costi per Categoria</CardTitle></CardHeader>
          <CardContent>
            {breakdown.length === 0 ? (
              <div className="h-[300px] flex items-center justify-center text-sm text-muted-foreground">Nessun costo registrato.</div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={breakdown} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={95}>
                      {breakdown.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => eur(v)} contentStyle={tooltipStyle} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-3 space-y-1.5">
                  {breakdown.map((b, i) => (
                    <div key={b.name} className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2 truncate">
                        <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                        <span className="truncate">{b.name}</span>
                      </span>
                      <span className="font-mono text-muted-foreground shrink-0">{totalCosti ? ((b.value / totalCosti) * 100).toFixed(0) : 0}%</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Redditività per Cantiere</CardTitle></CardHeader>
          <CardContent>
            {byCantiere.length === 0 ? (
              <div className="h-[300px] flex items-center justify-center text-sm text-muted-foreground">Nessun cantiere.</div>
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(300, byCantiere.length * 60)}>
                <BarChart data={byCantiere} layout="vertical" margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                  <XAxis type="number" tickFormatter={eurShort} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="nome" width={110} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                  <Tooltip formatter={(v, n) => [eur(v), n]} contentStyle={tooltipStyle} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {isOwner && <Bar dataKey="ricavi" name="Ricavi" fill="#10B981" radius={[0, 4, 4, 0]} />}
                  <Bar dataKey="costi" name="Costi" fill="#D97706" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Costi & Margini per Cantiere</CardTitle>
          <p className="text-xs text-muted-foreground">Manodopera, mezzi, subappaltatori e fatture per anno; budget confrontato col consuntivo cumulativo (tutti gli anni).</p>
        </CardHeader>
        <CardContent>
          {dettaglio.length === 0 ? (
            <div className="h-[120px] flex items-center justify-center text-sm text-muted-foreground">Nessun dato per l'anno selezionato.</div>
          ) : (
            <div className="overflow-x-auto -mx-2 px-2">
              <table className="w-full text-sm border-collapse" data-testid="cantiere-dettaglio-table">
                <thead>
                  <tr className="text-xs text-muted-foreground border-b border-border">
                    <th className="text-left font-medium py-2 pr-3 min-w-[150px]">Cantiere</th>
                    {isOwner && <th className="text-right font-medium py-2 px-2">Ricavi</th>}
                    <th className="text-right font-medium py-2 px-2">Manodopera</th>
                    <th className="text-right font-medium py-2 px-2">Mezzi</th>
                    <th className="text-right font-medium py-2 px-2">Subappalt.</th>
                    <th className="text-right font-medium py-2 px-2">Fatture</th>
                    <th className="text-right font-medium py-2 px-2">Costi</th>
                    <th className="text-right font-medium py-2 px-2">Sp. Gen. 15%</th>
                    <th className="text-right font-medium py-2 px-2 min-w-[130px]">Budget / Consuntivo</th>
                    <th className="text-right font-medium py-2 pl-2">Margine</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {dettaglio.map((r) => (
                    <tr key={r.id} data-testid={`cantiere-row-${r.id}`} className="hover:bg-secondary/40">
                      <td className="py-2.5 pr-3 font-medium truncate max-w-[200px]">{r.nome}</td>
                      {isOwner && <td className="py-2.5 px-2 text-right font-mono text-emerald-600">{eur(r.ricavi)}</td>}
                      <td className="py-2.5 px-2 text-right font-mono text-muted-foreground">{eur(r.manodopera)}</td>
                      <td className="py-2.5 px-2 text-right font-mono text-muted-foreground">{eur(r.mezzi)}</td>
                      <td className="py-2.5 px-2 text-right font-mono text-muted-foreground">{eur(r.subappaltatori)}</td>
                      <td className="py-2.5 px-2 text-right font-mono text-muted-foreground">{eur(r.fatture)}</td>
                      <td className="py-2.5 px-2 text-right font-mono text-red-600">{eur(r.costi)}</td>
                      <td className="py-2.5 px-2 text-right font-mono text-amber-600">{eur(r.spese_generali)}</td>
                      <td className="py-2.5 px-2 text-right" data-testid={`cantiere-budget-${r.id}`}>
                        {r.budget > 0 ? (
                          <div className="min-w-[120px] inline-block text-right">
                            <div className="font-mono text-xs text-muted-foreground">Budget {eur(r.budget)}</div>
                            <div className="h-1.5 rounded-full bg-secondary mt-1 overflow-hidden">
                              <div className={`h-full ${r.sforamento ? "bg-red-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(100, r.budget_usato_pct)}%` }} />
                            </div>
                            <div className={`text-[10px] mt-0.5 ${r.sforamento ? "text-red-600 font-semibold" : "text-muted-foreground"}`}>
                              {eur(r.consuntivo_totale)} · {r.budget_usato_pct}%{r.sforamento ? " · Sforato" : ""}
                            </div>
                          </div>
                        ) : <span className="text-xs text-muted-foreground">—</span>}
                      </td>
                      <td className={`py-2.5 pl-2 text-right font-mono font-semibold ${r.margine >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                        {eur(r.margine)}<span className="block text-[10px] font-normal text-muted-foreground">{r.margine_pct}%</span>
                        <span className="block text-[10px] font-normal text-muted-foreground">Netto {eur(r.margine_netto)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
